import { COLORS } from "../constants";
import DialogRouterView from "src/components/global/DialogRouterView.vue";
import { routeMatcher } from "route-matcher";
import { isBoolean, isString, zipObject } from "lodash";

let dialogRoutes = [];

// q-table virtual-scroll wraps rows in a `<tbody class="q-virtual-scroll__content"
// tabindex="-1">`. Clicking a row focuses that tbody. When QDialog opens, it
// captures the active element as its refocus target and calls `.focus()` on it
// (without `{preventScroll: true}`) when the dialog closes — scrolling the table
// down by the sticky header height. Blur the tbody before navigating so QDialog
// won't capture it. Refocus behavior is preserved for all other elements.
function blurVirtualScrollContent() {
  const active = document.activeElement;
  if (
    active &&
    active.classList &&
    active.classList.contains("q-virtual-scroll__content")
  ) {
    active.blur();
  }
}

export const DialogRouter = {
  get routes() {
    return dialogRoutes;
  },
  currentRoute: null,

  match(path, prependLocation = false) {
    if (isString(path)) {
      if (path && prependLocation) {
        path = location.pathname + path;
      }
    } else if ("name" in path) {
      path = this.stringify(path);
    }
    for (let i = 0; i < dialogRoutes.length; i++) {
      const route = dialogRoutes[i];
      const params = route.matcher.parse(path);
      if (params) {
        for (let param in params) {
          if (params.hasOwnProperty(param)) {
            params[param] = decodeURIComponent(params[param]);
          }
        }
        let meta = {};
        if (route.meta) {
          meta = Object.assign(meta, route.meta);
          if (meta.color && meta.color in COLORS) {
            Object.assign(meta, COLORS[meta.color]);
          }
        }
        return { ...route, meta, params };
      }
    }
    if (!prependLocation) {
      return this.match(path, true);
    }
    return null;
  },

  stringify({ name, params }) {
    const route = dialogRoutes.find((route) => route.name === name);
    if (!route) {
      throw "Dialog route not found: " + name;
    }
    if (params) {
      for (let param in params) {
        if (params.hasOwnProperty(param)) {
          params[param] = encodeURIComponent(params[param]);
        }
      }
    }
    let path = route.matcher.stringify(params);
    if (path.startsWith(location.pathname + "/")) {
      path = path.slice(location.pathname.length);
    }
    return path;
  },

  push({ name, params }) {
    blurVirtualScrollContent();
    window.location.hash = name ? this.stringify({ name, params }) : "";
  },

  replace({ name, params }) {
    blurVirtualScrollContent();
    window.location.replace(
      window.location.pathname +
        (name ? "#" + this.stringify({ name, params }) : "")
    );
  },

  install(app) {
    app.component("dialog-router-view", DialogRouterView);

    app.config.globalProperties.$dialogs = DialogRouter;
    app.config.globalProperties.$dialog = null;

    // Update $dialog when currentRoute changes
    Object.defineProperty(DialogRouter, "currentRoute", {
      set(value) {
        this._currentRoute = value;
        app.config.globalProperties.$dialog = value;
      },
      get() {
        return this._currentRoute;
      },
    });
  },
};

export function mapRoutes(dialogs) {
  return zipObject(
    Object.keys(dialogs),
    Object.values(dialogs).map((path) => ({
      get() {
        if (path.endsWith("/")) {
          return this.$route.hash.startsWith("#" + path);
        } else {
          return this.$route.hash === "#" + path;
        }
      },
      set(value) {
        if (value) {
          if (!isBoolean(value) && path.endsWith("/")) {
            window.location.hash = path + value;
          } else {
            window.location.hash = path;
          }
        } else {
          // Only navigate back if we're still on this dialog's hash.
          // When a new dialog opens on top (e.g., clicking a row in the
          // duplicates dialog pushes a contact-view hash), the reactive
          // getter flips to false and SmallDialog's watcher calls
          // QDialog.hide(). After the animation, QDialog emits
          // update:modelValue=false, which flows back through this
          // setter. Calling $router.back() at that point would pop the
          // newly pushed dialog hash too — losing the contact-view
          // route and bouncing the user back to /duplicates. Compare
          // the current hash against this dialog's path; if we've
          // already moved past it, treat the close as a no-op.
          const currentHash = this.$route.hash;
          const stillHere = path.endsWith("/")
            ? currentHash.startsWith("#" + path)
            : currentHash === "#" + path;
          if (stillHere) {
            this.$router.back();
          }
        }
      },
    }))
  );
}

export default async ({ app }) => {
  // Dynamically import dialogs to avoid circular dependency
  const { dialogs } = await import("src/router/routes");
  dialogRoutes = dialogs;
  dialogRoutes.forEach((route) => (route.matcher = routeMatcher(route.path)));
  app.use(DialogRouter);
};
